import { useState, useEffect, useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CommentsSection({ postId, canComment = true }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/posts/${postId}/comments`, { withCredentials: true });
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/posts/${postId}/comments`,
        { content: newComment.trim() },
        { withCredentials: true }
      );
      setComments(prev => [...prev, response.data]);
      setNewComment('');
    } catch (error) {
      toast.error('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateStr) => {
    try {
      return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-stone-100" data-testid="comments-section">
      {/* Comments List */}
      <div className="space-y-4 mb-4 max-h-60 overflow-y-auto">
        {loading ? (
          <div className="text-center py-4">
            <Loader2 className="w-5 h-5 text-teal-600 animate-spin mx-auto" />
          </div>
        ) : comments.length === 0 ? (
          <p className="text-sm text-stone-500 text-center py-2">No comments yet</p>
        ) : (
          comments.map(comment => (
            <div key={comment.id} className="flex gap-3" data-testid={`comment-${comment.id}`}>
              <Avatar className="h-8 w-8 flex-shrink-0">
                <AvatarFallback className="bg-stone-200 text-stone-600 text-xs">
                  {getInitials(comment.user_name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="bg-stone-50 rounded-2xl rounded-tl-sm px-4 py-2">
                  <p className="font-medium text-sm text-stone-900">{comment.user_name}</p>
                  <p className="text-sm text-stone-700">{comment.content}</p>
                </div>
                <p className="text-xs text-stone-500 mt-1 ml-2">{formatDate(comment.created_at)}</p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* New Comment Form */}
      {canComment && (
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Input
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Write a comment..."
            className="flex-1 rounded-full border-stone-200 focus:border-teal-500 focus:ring-teal-500"
            data-testid="comment-input"
          />
          <Button
            type="submit"
            size="icon"
            disabled={submitting || !newComment.trim()}
            className="bg-teal-600 hover:bg-teal-700 text-white rounded-full h-10 w-10"
            data-testid="submit-comment-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </form>
      )}
    </div>
  );
}
